/**
 * TokenAuthFailedBanner (Story 32-5 — ADR-0004 §Failure-State Taxonomy,
 * `auth_failed` row)
 *
 * ONE persistent, non-dismissible banner row PER failed OAuth token (the
 * per-token API shape makes per-integration duplication impossible by
 * construction). A single "Reconnect {Provider}" click relaunches the OAuth
 * flow and restores ALL N integrations riding that grant.
 *
 * Auto-clear is two-layered: the query refetches on window focus (the
 * instant-clear path when the user returns from the OAuth tab) and polls
 * every 30s WHILE failures exist (conditional `refetchInterval`); the server
 * side is closed by `propagateReauthRecovery` firing from
 * `catalogDiscoverAndAttach` on every successful re-auth.
 *
 * Always mount this component — it renders no visible UI when the list is
 * empty, and the mounted `AnimatePresence` container is what lets the exit
 * animation play on auto-clear (directive §4.6 rationale: a page-level
 * conditional unmount would kill it).
 *
 * Token axis ONLY: `resource_unavailable` outages render NOTHING here
 * (Story 32-4's banner owns that axis).
 */

import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { motion, AnimatePresence } from "motion/react";
import { Warning, ArrowsClockwise, ArrowSquareOut } from "@phosphor-icons/react";
import { Button } from "@provenleap/ui";
import { platformDisplayName } from "@provenleap/integrations";
import { apiGet } from "../../lib/apiClient";
import { useAuth } from "../../stores/authStore";
import {
  useIntegrationConnect,
  isConnectDispatchable,
} from "../../hooks/useIntegrationConnect";
import type { ToastType } from "../../hooks/useToast";
import type { FailedTokenSummary } from "./types";

interface TokenAuthFailedBannerProps {
  clientId: string;
  showToast: (type: ToastType, message: string) => void;
  /**
   * Fallback CTA target for platforms `connect()` cannot dispatch (api-key
   * families can reach `revoked` via the failing-tokens cron) — the page
   * passes `() => setActiveTab("integrations")`. Never a silent no-op.
   */
  onOpenIntegrations?: () => void;
}

/**
 * AC #9 conditional polling (TanStack v5 function form): 30s while failures
 * exist, off when clear. Pure + exported so the truth table is pinned by a
 * unit test (the inline closure was untestable). On a refetch ERROR the query
 * retains its last successful data (v5 default), so an established banner
 * keeps polling through transient API blips.
 */
export function failedTokensPollInterval(
  data: readonly FailedTokenSummary[] | undefined
): number | false {
  return data && data.length > 0 ? 30_000 : false;
}

/** "GA4 — Acme main, Google Ads — Acme" (AC #10). */
function affectedListLabel(entry: FailedTokenSummary): string {
  return entry.affectedIntegrations
    .map(
      (i) =>
        platformDisplayName(i.platform) +
        (i.accountName ? ` — ${i.accountName}` : "")
    )
    .join(", ");
}

export function TokenAuthFailedBanner({
  clientId,
  showToast,
  onOpenIntegrations,
}: TokenAuthFailedBannerProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canReconnect = user?.role === "owner" || user?.role === "admin";

  const { data } = useQuery<FailedTokenSummary[]>({
    queryKey: ["failed-tokens", clientId],
    queryFn: async () => {
      const res = await apiGet(
        `/api/clients/${clientId}/integrations/failed-tokens`
      );
      if (!res.ok) {
        // Persistent-banner contract (ADR-0004: "Cleared only when the
        // underlying status transitions back to OK"): a transient API error
        // must NEVER clear the banner. Throwing keeps the last successful
        // data (TanStack v5 retains data on refetch error) — and therefore
        // the 30s poll — alive; returning [] here would dismiss the banner
        // AND stop polling until the next window focus. (Senior review F1.)
        throw new Error(`failed-tokens fetch failed: ${res.status}`);
      }
      const json = (await res.json()) as { data: FailedTokenSummary[] };
      return json.data;
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    // TanStack v5 function form: poll only while failures exist. The default
    // refetchOnWindowFocus stays ON — it is the instant-clear path when the
    // user returns from the OAuth tab/popup.
    refetchInterval: (query) => failedTokensPollInterval(query.state.data),
  });

  const entries = data ?? [];

  const { connect, connectingPlatform, dialogs } = useIntegrationConnect({
    clientId,
    connectedIntegrations: [],
    showToast,
    onIntegrationChange: () => {
      queryClient.invalidateQueries({ queryKey: ["failed-tokens", clientId] });
      queryClient.invalidateQueries({
        queryKey: ["client-integrations", clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ["client-connections", clientId],
      });
    },
  });

  return (
    <>
      {/* Spacing only while the container HAS rows (incl. exiting ones):
          empty = zero-height, layout byte-identical to pre-32-5. The 44px
          bottom margin clears the ClientTabBar notch's -36px pull. */}
      <div
        className="space-y-3 [&:has(>*)]:mt-4 [&:has(>*)]:mb-[44px]"
        data-testid="token-auth-failed-banner"
      >
        <AnimatePresence initial={false}>
          {entries.map((entry) => {
            const ctaPlatform = entry.affectedIntegrations[0]?.platform;
            const dispatchable =
              !!ctaPlatform && isConnectDispatchable(ctaPlatform);
            const pending =
              !!ctaPlatform && connectingPlatform === ctaPlatform;
            const accessibleName = `Reconnect ${entry.providerLabel}${
              entry.providerAccountName
                ? ` (${entry.providerAccountName})`
                : ""
            }`;
            return (
              <motion.div
                key={entry.tokenId}
                role="alert"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="rounded-[14px] border-[0.5px] border-destructive/50 bg-destructive/5 px-4 py-3 shadow-xs"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-2">
                    <Warning
                      size={18}
                      className="mt-0.5 shrink-0 text-destructive"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-destructive">
                        {entry.providerLabel} connection lost
                        {entry.providerAccountName
                          ? ` — ${entry.providerAccountName}`
                          : ""}
                      </p>
                      <p className="text-[13px] text-foreground">
                        {entry.affectedCount === 1
                          ? "Reconnect once to restore the affected integration."
                          : `Reconnect once to restore all ${entry.affectedCount} affected integrations.`}
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {affectedListLabel(entry)}
                      </p>
                    </div>
                  </div>
                  {!canReconnect ? (
                    <p className="shrink-0 self-center text-[13px] italic text-muted-foreground">
                      Ask an owner or admin to reconnect {entry.providerLabel}
                      .
                    </p>
                  ) : dispatchable ? (
                    <Button
                      type="button"
                      variant="outline"
                      aria-label={accessibleName}
                      disabled={pending}
                      onClick={() => connect(ctaPlatform!)}
                      className="shrink-0 self-center rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
                      style={{ border: "0.5px solid var(--hairline)" }}
                    >
                      <ArrowsClockwise
                        size={16}
                        className={`mr-1.5 ${pending ? "animate-spin" : ""}`}
                      />
                      {pending
                        ? "Reconnecting…"
                        : `Reconnect ${entry.providerLabel}`}
                    </Button>
                  ) : onOpenIntegrations ? (
                    <Button
                      type="button"
                      variant="outline"
                      aria-label={`Open Integrations to reconnect ${entry.providerLabel}`}
                      onClick={onOpenIntegrations}
                      className="shrink-0 self-center rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
                      style={{ border: "0.5px solid var(--hairline)" }}
                    >
                      <ArrowSquareOut size={16} className="mr-1.5" />
                      Open Integrations
                    </Button>
                  ) : (
                    // No navigation callback wired (defensive — the $clientId
                    // mount always passes one): a dead button is worse than a
                    // plain instruction (AC #11 "never a silent no-op").
                    <p className="shrink-0 self-center text-[13px] text-muted-foreground">
                      Reconnect {entry.providerLabel} from the Integrations
                      tab.
                    </p>
                  )}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
      {dialogs}
    </>
  );
}
